import React, { createContext, useContext, useCallback, useState } from 'react';
import { useAuth } from './AuthContext';

// Create context
const QuoteContext = createContext();

// Provider component
export const QuoteProvider = ({ children }) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Submit quote request - independent of booking system
  const submitQuoteRequest = useCallback(async (customerInfo, quoteInfo) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Use the existing quick-quote endpoint
      const emailPayload = {
        name: `${customerInfo.firstName || ''} ${customerInfo.lastName || ''}`.trim() || customerInfo.name || '',
        email: customerInfo.email || '',
        phone: customerInfo.phone || '',
        address: quoteInfo.address || customerInfo.address || '',
        message: generateQuoteMessage(customerInfo, quoteInfo),
        tenantId: process.env.REACT_APP_TENANT_ID || 1,
        cityCode: process.env.REACT_APP_CITY_CODE || 'MCO'
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/contact/quick-quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailPayload)
      });

      if (!response.ok) {
        throw new Error(`Failed to send quote request: ${response.statusText}`);
      }

      // Check if response is HTML (common in production routing issues)
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        if (text.includes('<!doctype') || text.includes('<html')) {
          throw new Error('API endpoint not found. Please check your backend configuration.');
        }
        throw new Error('Invalid response format received from server');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to send quote request');
      }
      
      console.log('✅ Quote request sent successfully');
      return { success: true, result };
      
    } catch (error) {
      console.error('❌ Quote submission error:', error);
      setSubmitError(error.message);
      return { success: false, error: error.message };
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  // Generate a formatted message for the quote request
  const generateQuoteMessage = (customerInfo, quoteInfo) => {
    const parts = [];
    
    // Customer information
    if (customerInfo.name || (customerInfo.firstName && customerInfo.lastName)) {
      const fullName = customerInfo.name || `${customerInfo.firstName} ${customerInfo.lastName}`;
      parts.push(`Customer: ${fullName}`);
    }
    
    if (customerInfo.email) {
      parts.push(`Email: ${customerInfo.email}`);
    }
    
    if (customerInfo.phone) {
      parts.push(`Phone: ${customerInfo.phone}`);
    }

    // Property information
    if (quoteInfo.residenceType) {
      parts.push(`Residence Type: ${quoteInfo.residenceType}`);
    }
    
    if (quoteInfo.bedrooms && quoteInfo.bedrooms !== 'Office') {
      parts.push(`${quoteInfo.bedrooms} bedroom(s)`);
    }
    
    if (quoteInfo.bathrooms && quoteInfo.bathrooms !== 'Office') {
      parts.push(`${quoteInfo.bathrooms} bathroom(s)`);
    }
    
    if (quoteInfo.squareFootage) {
      parts.push(`${quoteInfo.squareFootage} sq ft`);
    }
    
    if (quoteInfo.frequency) {
      parts.push(`Frequency: ${quoteInfo.frequency}`);
    }
    
    if (quoteInfo.cleanByDate) {
      const cleanByDate = new Date(quoteInfo.cleanByDate);
      const formattedDate = cleanByDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      parts.push(`Clean by date: ${formattedDate}`);
    }
    
    if (quoteInfo.lastCleaned) {
      parts.push(`Last cleaned: ${quoteInfo.lastCleaned}`);
    }
    
    if (quoteInfo.cleanlinessLevel) {
      parts.push(`Cleanliness level: ${quoteInfo.cleanlinessLevel}/10`);
    }
    
    if (quoteInfo.address || customerInfo.address) {
      parts.push(`Address: ${quoteInfo.address || customerInfo.address}`);
    }
    
    if (quoteInfo.accessInstructions) {
      parts.push(`Access instructions: ${quoteInfo.accessInstructions}`);
    }
    
    if (quoteInfo.decisionMaker) {
      let hasAdditionalDecisionMaker = false;
      
      if (typeof quoteInfo.decisionMaker === 'object') {
        // Check if the option indicates additional decision maker
        hasAdditionalDecisionMaker = quoteInfo.decisionMaker.option && 
          quoteInfo.decisionMaker.option !== "I'm the only decision maker";
      } else if (typeof quoteInfo.decisionMaker === 'string') {
        hasAdditionalDecisionMaker = quoteInfo.decisionMaker !== "I'm the only decision maker";
      }
      
      parts.push(`Additional decision maker: ${hasAdditionalDecisionMaker ? 'Yes' : 'No'}`);
    }

    // Special occasion details
    if (quoteInfo.specialOccasion && typeof quoteInfo.specialOccasion === 'object') {
      if (quoteInfo.specialOccasion.followUp) {
        parts.push(`Special occasion details: ${quoteInfo.specialOccasion.followUp}`);
      }
    }
    
    return parts.join('\n');
  };

  const value = {
    // State
    isSubmitting,
    submitError,
    
    // Actions
    submitQuoteRequest,
    
    // Computed
    isAuthenticated: !!user
  };

  return (
    <QuoteContext.Provider value={value}>
      {children}
    </QuoteContext.Provider>
  );
};

// Hook to use quote context
export const useQuote = () => {
  const context = useContext(QuoteContext);
  if (!context) {
    throw new Error('useQuote must be used within a QuoteProvider');
  }
  return context;
};

export default QuoteContext;